import React, {
  type FC,
  type ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaTiktok,
  FaYoutube,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { HiLocationMarker } from "react-icons/hi";
import { OutboundLink } from "gatsby-plugin-google-gtag";
import { Link, useI18next } from "gatsby-plugin-react-i18next";

import AmexIcon from "@components/icons/card_logo/amex_icon";
import ApplePayIcon from "@components/icons/card_logo/apple_pay_icon";
import GooglePayIcon from "@components/icons/card_logo/google_pay_icon";
import MastercardIcon from "@components/icons/card_logo/mastercard_icon";
import VisaIcon from "@components/icons/card_logo/visa_icon";
import { useAffiliate } from "@hooks/affiliate";
import type Country from "@interfaces/Country";
import {
  type ConfigurationState,
  useConfigurationState,
} from "@stores/configuration";
import { getAffiliateLogoUrl } from "@utilities/affiliate";
import { getStoreIcons } from "@utilities/helper";
import { getCountryCodes, getCountryContent } from "@utilities/pricing-data";
import {
  affiliateRestrictedCountries,
  hiddenCountriesToPurchaseOwnPlan,
} from "@utilities/restrictions";

type FooterKey = "resources" | "guides" | "company";
interface FooterGroup {
  title: string;
  items: FooterItem[];
}
interface FooterItem {
  type: "internal" | "external";
  href: string;
  label: string;
}

type FooterSection = {
  key: FooterKey;
  title: string;
} & {
  type: "links";
  items: FooterItem[];
};

const FooterLinkItem: FC<{ item: FooterItem; className?: string }> = ({
  item,
  className,
}) => {
  if (item.type === "internal") {
    return (
      <Link to={item.href} className={className}>
        {item.label}
      </Link>
    );
  }
  return (
    <OutboundLink href={item.href} className={className}>
      {item.label}
    </OutboundLink>
  );
};

const countriesKeys = getCountryCodes();

const loadPopularLocations = (
  language: string = "en",
  configurationState: ConfigurationState,
  isIncludedInHiddenCountries: boolean = false,
): Country[] => {
  const loadedCountries: Country[] = [];
  countriesKeys.forEach(id => {
    const content = getCountryContent(id, language);
    if (!content) return;
    if (
      isIncludedInHiddenCountries &&
      configurationState.countryCodeFromApi.toLowerCase() == id.toLowerCase()
    ) {
      return;
    }
    if (
      affiliateRestrictedCountries[configurationState.affiliate] &&
      affiliateRestrictedCountries[configurationState.affiliate].includes(
        id.toUpperCase(),
      )
    ) {
      return;
    }
    if (content.popularity_position) {
      loadedCountries.push({
        id,
        content,
      });
    }
  });
  loadedCountries.sort((a, b) => {
    const popA = parseInt(a.content.popularity_position ?? "99");
    const popB = parseInt(b.content.popularity_position ?? "99");

    if (popA - popB < 0) return -1;
    if (popA - popB > 0) return 1;

    // Third priority: country_name
    const countryA = a.content.country_name ?? "";
    const countryB = b.content.country_name ?? "";

    if (countryA < countryB) return -1;
    if (countryA > countryB) return 1;

    // If all criteria are the same, return 0
    return 0;
  });

  return loadedCountries;
};

const Footer = (): ReactElement => {
  const { t, language } = useI18next();
  const { affiliate, isResolvingAffiliate } = useAffiliate();
  const affiliateLogoUrl = affiliate ? getAffiliateLogoUrl(affiliate) : "";
  const configurationState = useConfigurationState();
  const [isIncludedInHiddenCountries, setIncludedInHiddenCountries] =
    useState<boolean>(false);

  const maxPopularLocations = 7;
  const [popularLocations, setPopularLocations] = useState<Country[]>([]);
  useEffect(() => {
    setIncludedInHiddenCountries(
      hiddenCountriesToPurchaseOwnPlan().includes(
        configurationState.countryCodeFromApi.toUpperCase(),
      ),
    );
  }, [configurationState.countryCodeFromApi]);
  useEffect(() => {
    const loadedLocations = loadPopularLocations(
      language,
      configurationState,
      isIncludedInHiddenCountries,
    );
    setPopularLocations(loadedLocations);
  }, [language, configurationState, isIncludedInHiddenCountries]);

  const footerSections: FooterSection[] = useMemo(
    () => [
      {
        key: "resources",
        title: t("resources"),
        type: "links",
        items: [
          {
            type: "internal",
            href: "/sim-cards",
            label: t("sim_cards"),
          },
          {
            type: "internal",
            href: "/bne-go-portable-esim-wifi-router",
            label: "BNE Go Pocket WiFi",
          },
          {
            type: "external",
            href: "https://www.bnesim.com/category/all/",
            label: t("blog"),
          },
          {
            type: "external",
            href: "https://www.bnesim.com/help/",
            label: t("help_center"),
          },
          {
            type: "external",
            href: "https://status.bnesim.com/",
            label: t("service_status"),
          },
        ],
      },
      {
        key: "guides",
        title: t("featured_guides"),
        type: "links",
        items: [
          {
            type: "internal",
            href: "/compatible-devices-esim/",
            label: t("esim_compatible_devices"),
          },
          {
            type: "internal",
            href: "/how-to-install-bne-esim-on-your-iphone/",
            label: t("install_esim_on_iphone"),
          },
          {
            type: "external",
            href: "https://www.bnesim.com/how-to-install-bne-esim-on-your-ipad/",
            label: t("install_esim_on_ipad"),
          },
          {
            type: "internal",
            href: "/how-to-install-bne-esim-on-your-android/",
            label: t("install_esim_on_android"),
          },
          {
            type: "external",
            href: "https://www.bnesim.com/international-sim-card-install-bnesim-x-4g-lte-sim-card-ios-no-credentials/",
            label: t("install_sim_card_on_iphone"),
          },
          {
            type: "external",
            href: "https://www.bnesim.com/international-sim-card-install-bnesim-x-4g-lte-sim-card-android-no-credentials/",
            label: t("install_sim_card_on_android"),
          },
          {
            type: "internal",
            href: "/manage-your-esim-and-sim-card-with-the-bnesim-app/",
            label: t("manage_esim_and_sim_card_with_bne_app"),
          },
        ],
      },
      {
        key: "company",
        title: t("company"),
        type: "links",
        items: [
          {
            type: "internal",
            href: "/partner-with-bnesim/",
            label: t("partnership"),
          },
          {
            type: "internal",
            href: "/about-bnesim/",
            label: t("about_us"),
          },
          {
            type: "internal",
            href: "/contacts/",
            label: t("contact_us"),
          },
          {
            type: "external",
            href: "https://www.bnesim.com/press-release/",
            label: t("press_release"),
          },
          {
            type: "external",
            href: "https://bounty.bnesim.com/",
            label: t("security_program"),
          },
        ],
      },
    ],
    [language],
  );

  const [appStoreIcon, setAppStoreIcon] = useState<string>("");
  const [playStoreIcon, setPlayStoreIcon] = useState<string>("");
  useEffect(() => {
    loadStoreIcons();
  }, []);
  const loadStoreIcons = () => {
    const { appStore, playStore } = getStoreIcons(language);
    setAppStoreIcon(appStore);
    setPlayStoreIcon(playStore);
  };

  const socialMediaLinks: Array<{
    id: string;
    href: string;
    icon: ReactElement;
  }> = useMemo(
    () => [
      {
        id: "facebook",
        href: "https://www.facebook.com/bnesim.official/",
        icon: <FaFacebook className="size-full" />,
      },
      {
        id: "instagram",
        href: "https://www.instagram.com/bne_sim/",
        icon: <FaInstagram className="size-full" />,
      },
      {
        id: "tiktok",
        href: "https://www.tiktok.com/@bne_sim",
        icon: <FaTiktok className="size-full" />,
      },
      {
        id: "youtube",
        href: "https://www.youtube.com/channel/UCFy5Zaf3pW4buWr1EpZbJ4g",
        icon: <FaYoutube className="size-full" />,
      },
      {
        id: "linkedin",
        href: "https://www.linkedin.com/company/bnesim/",
        icon: <FaLinkedin className="size-full" />,
      },
      {
        id: "xtwitter",
        href: "https://x.com/BNE_SIM/",
        icon: <FaXTwitter className="size-full" />,
      },
    ],
    [],
  );

  return (
    <footer className="mt-20 bg-[#f5f5f5] py-4 sm:py-6">
      <div className="container flex flex-col gap-6">
        <div className="flex flex-row flex-wrap items-start gap-4 md:grid md:grid-cols-[1.6fr_repeat(4,1fr)]">
          <div className="flex w-full flex-col items-start gap-4 md:w-auto">
            {isResolvingAffiliate ? (
              <div
                className="h-[27px] w-[117px] shrink-0 animate-pulse rounded-md bg-gray-200 md:h-[43px] md:w-[187px]"
                aria-hidden
              />
            ) : affiliate !== null ? (
              <OutboundLink
                href={affiliate.website || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="h-fit shrink-0 text-primary-100 transition duration-300 hover:opacity-80 focus:opacity-80 focus:outline-none"
              >
                <img
                  src={affiliateLogoUrl || "/images/bnesim_logo.svg"}
                  className="h-[27px] w-auto md:h-[43px]"
                  loading="eager"
                  width={300}
                  height={69}
                  alt={`${affiliate.name} logo`}
                />
              </OutboundLink>
            ) : (
              <Link
                to="/"
                className="h-fit shrink-0 text-primary-100 transition duration-300 hover:opacity-80 focus:opacity-80 focus:outline-none"
              >
                <img
                  src="/images/bnesim_logo.svg"
                  className="h-[27px] w-auto md:h-[43px]"
                  loading="eager"
                  width={300}
                  height={69}
                  alt="Official BNESIM Logo – A sleek modern design representing the world’s best travel eSIM provider for global connectivity and seamless communication."
                />
              </Link>
            )}
            <p className="mb-0 text-start text-sm text-black md:text-base">
              {t("footer_desc", {
                appName: affiliate?.name ?? process.env.GATSBY_APP_NAME ?? "",
              })}
            </p>
            {affiliate && (
              <div className="flex w-fit flex-row items-center overflow-hidden rounded-xl border border-gray-300 bg-white p-1 text-xs lg:text-sm">
                <span className="text-nowrap rounded-lg bg-primary px-2 py-1 font-medium text-white">
                  {t("powered_by")}
                </span>
                <div className="flex flex-row items-center gap-1 px-2 py-1">
                  <img
                    src="/images/bne_site_icon.svg"
                    className="size-4 lg:size-5"
                    width={20}
                    height={20}
                    alt="BNESIM icon"
                  />
                  <span className="font-medium text-primary">
                    {process.env.GATSBY_APP_NAME}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="flex w-full max-w-[calc(50%-1rem)] flex-col gap-4 md:w-auto md:max-w-none">
            <h3 className="m-0 text-base font-medium uppercase lg:text-lg">
              {t("popular_destinations")}
            </h3>
            <div className="flex flex-col gap-2">
              {popularLocations.slice(0, maxPopularLocations).map(country => (
                <Link
                  key={country.id}
                  to={`/plans/${country.id.toLowerCase()}`}
                  className="text-sm font-normal text-black no-underline transition-colors duration-300 hover:text-primary focus:text-primary focus:outline-none md:text-base"
                >
                  {country.content.country_name}
                </Link>
              ))}
            </div>
          </div>
          {footerSections.map(section => (
            <div
              key={section.key}
              className="flex w-full max-w-[calc(50%-1rem)] flex-col gap-4 md:w-auto md:max-w-none"
            >
              <h3 className="m-0 text-base font-medium uppercase lg:text-lg">
                {section.title}
              </h3>
              <div className="flex flex-col gap-2">
                {section.items.map((item, index) => (
                  <FooterLinkItem
                    key={index}
                    item={item}
                    className="text-sm font-normal text-black no-underline transition-colors duration-300 hover:text-primary focus:text-primary focus:outline-none md:text-base"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-row items-center justify-center gap-4">
          <OutboundLink
            className="group h-[48px] w-max focus:outline-none"
            href={process.env.GATSBY_APP_STORE_LINK}
            aria-label="go to App Store"
            target="_blank"
          >
            {appStoreIcon ? (
              <img
                src={appStoreIcon}
                width={1920}
                height={600}
                className="h-full w-auto transition duration-300 group-hover:scale-105 group-focus:scale-95"
                alt="Download on the App Store"
              />
            ) : (
              <div className="h-full w-[210px] animate-pulse rounded-md bg-gray-200"></div>
            )}
          </OutboundLink>
          <OutboundLink
            className="group h-[48px] w-max focus:outline-none"
            href={process.env.GATSBY_PLAY_STORE_LINK}
            aria-label="go to Play Store"
            target="_blank"
          >
            {playStoreIcon ? (
              <img
                src={playStoreIcon}
                width={1920}
                height={600}
                className="h-full w-auto transition duration-300 group-hover:scale-105 group-focus:scale-95"
                alt="Get it on Google Play"
              />
            ) : (
              <div className="h-full w-[210px] animate-pulse rounded-md bg-gray-200"></div>
            )}
          </OutboundLink>
        </div>
        <div className="flex flex-col items-center justify-center gap-2">
          <div className="flex flex-row items-start justify-center gap-1 sm:items-center">
            <HiLocationMarker className="size-4 shrink-0 text-primary" />
            <p className="mb-0 text-start text-sm text-black">
              {t("address_content")}
            </p>
          </div>
          <p className="mb-0 text-start text-sm text-black">
            {t("copyright_content", { year: new Date().getFullYear() })}
          </p>
        </div>
        <div className="flex w-full flex-col items-center justify-center gap-4 md:flex-row md:justify-between">
          <div className="flex flex-row flex-wrap items-center gap-2 sm:gap-4">
            {socialMediaLinks.map(link => (
              <OutboundLink
                key={link.id}
                href={link.href}
                className="size-4 text-black transition hover:text-primary focus:text-primary focus:outline-none md:size-5"
              >
                {link.icon}
              </OutboundLink>
            ))}
          </div>
          <div className="flex flex-col flex-wrap items-center sm:flex-row">
            <OutboundLink
              href={process.env.GATSBY_PRIVACY_POLICY_LINK}
              target="_blank"
              rel="noreferrer"
              className="custom-footer-legal-link"
            >
              {t("privacy_policy")}
            </OutboundLink>
            <OutboundLink
              href={process.env.GATSBY_TERMS_OF_SERVICE_LINK}
              target="_blank"
              rel="noreferrer"
              className="custom-footer-legal-link"
            >
              {t("terms_and_conditions")}
            </OutboundLink>
            <OutboundLink
              href={process.env.GATSBY_FAIR_USE_POLICY_LINK}
              target="_blank"
              rel="noreferrer"
              className="custom-footer-legal-link"
            >
              {t("fair_use_policy")}
            </OutboundLink>
          </div>
        </div>
        <hr className="-mx-2 mb-0" />
        <div className="flex flex-row flex-wrap items-center justify-center gap-2">
          <ApplePayIcon className="h-7 shrink-0" />
          <hr className="mx-3 my-0 h-4 w-px rounded" />
          <GooglePayIcon className="h-8 shrink-0" />
          <hr className="mx-3 my-0 h-4 w-px rounded" />
          <VisaIcon className="h-8 shrink-0" />
          <hr className="mx-3 my-0 h-4 w-px rounded" />
          <MastercardIcon className="h-8 shrink-0" />
          <hr className="mx-3 my-0 h-4 w-px rounded" />
          <AmexIcon className="h-8 shrink-0" />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
